import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Wand2, 
  Copy, 
  Download, 
  Save, 
  AlertCircle, 
  Check, 
  ChevronDown, 
  X, 
  Globe,
  RefreshCw,
  Gift,
  EyeOff,
  FileText,
  Paperclip,
  Trash
} from 'lucide-react';
import { generateJobPost } from '../lib/chatbase';
import { User, ContractType, PostTone, JobDocument } from '../lib/types';
import { decrementCredits, hasRemainingCredits, saveJobPost } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { extractTextFromPDF } from '../lib/pdf';
import LinkedInShareButton from './LinkedInShareButton';
import TextEditor from './TextEditor';

interface JobPostGeneratorProps {
  user: User;
}

const JobPostGenerator: React.FC<JobPostGeneratorProps> = ({ user }) => {
  const [companyName, setCompanyName] = useState('');
  const [position, setPosition] = useState('');
  const [location, setLocation] = useState('');
  const [experience, setExperience] = useState('');
  const [skills, setSkills] = useState('');
  const [contractType, setContractType] = useState<ContractType>('CDI');
  const [tone, setTone] = useState<PostTone>('Formelle');
  const [jobLink, setJobLink] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyValues, setCompanyValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState('');
  
  const [generatedPost, setGeneratedPost] = useState('');
  const [editableText, setEditableText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [isToneModalOpen, setIsToneModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  
  const [benefits, setBenefits] = useState<JobBenefit[]>([
    { id: 'car', label: 'Voiture de fonction', icon: '🚗', selected: false },
    { id: 'phone', label: 'Téléphone portable', icon: '📱', selected: false },
    { id: 'food', label: 'Tickets restaurant', icon: '🍽️', selected: false },
    { id: 'remote', label: 'Télétravail', icon: '🏠', selected: false },
    { id: 'health', label: 'Mutuelle santé', icon: '🏥', selected: false },
    { id: 'gym', label: 'Salle de sport', icon: '🏋️‍♂️', selected: false },
    { id: 'training', label: 'Formation continue', icon: '📚', selected: false },
    { id: 'bonus', label: 'Prime annuelle', icon: '💰', selected: false }
  ]);

  const [isBlinded, setIsBlinded] = useState(false);
  const [sector, setSector] = useState('');
  
  const [documents, setDocuments] = useState<JobDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const toneModalRef = useRef<HTMLDivElement>(null);
  const contractModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toneModalRef.current && !toneModalRef.current.contains(event.target as Node)) {
        setIsToneModalOpen(false);
      }
      if (contractModalRef.current && !contractModalRef.current.contains(event.target as Node)) {
        setIsContractModalOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setIsUploading(true);
    setError(null);

    try {
      const newDocuments: JobDocument[] = [];

      for (const file of acceptedFiles) {
        if (file.type !== 'application/pdf') {
          throw new Error('Seuls les fichiers PDF sont acceptés');
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
          throw new Error('La taille du fichier ne doit pas dépasser 5MB');
        }

        // Extract text from PDF
        const extractedText = await extractTextFromPDF(file);

        const fileName = `${Date.now()}-${file.name}`;
        const { data, error } = await supabase.storage
          .from('job-documents')
          .upload(`${user.id}/${fileName}`, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('job-documents')
          .getPublicUrl(data.path);

        newDocuments.push({
          name: file.name,
          url: publicUrl,
          type: file.type,
          size: file.size,
          content: extractedText // Store the extracted text
        });
      }

      setDocuments(prev => [...prev, ...newDocuments]);
      setSuccess('Documents ajoutés avec succès');
    } catch (error: any) {
      setError(error.message || 'Erreur lors du téléchargement des documents');
    } finally {
      setIsUploading(false);
    }
  }, [user.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const removeDocument = async (doc: JobDocument) => {
    try {
      const fileName = doc.url.split('/').pop();
      if (!fileName) throw new Error('Nom de fichier invalide');

      const { error } = await supabase.storage
        .from('job-documents')
        .remove([`${user.id}/${fileName}`]);

      if (error) throw error;

      setDocuments(prev => prev.filter(d => d.url !== doc.url));
      setSuccess('Document supprimé avec succès');
    } catch (error: any) {
      setError(error.message || 'Erreur lors de la suppression du document');
    }
  };

  const handleAddValue = () => {
    if (newValue.trim() && companyValues.length < 5) {
      setCompanyValues([...companyValues, newValue.trim()]);
      setNewValue('');
    }
  };
  
  const handleRemoveValue = (index: number) => {
    setCompanyValues(companyValues.filter((_, i) => i !== index));
  };

  const toggleBenefit = (benefitId: string) => {
    setBenefits(benefits.map(benefit => 
      benefit.id === benefitId 
        ? { ...benefit, selected: !benefit.selected }
        : benefit
    ));
  };

  const handleGenerate = async () => {
    if (!user?.id) {
      setError('Veuillez vous connecter pour générer une offre');
      return;
    }

    const hasCredits = await hasRemainingCredits(user.id);
    if (!hasCredits) {
      setError('Vous avez épuisé vos crédits. Veuillez passer à un forfait supérieur.');
      return;
    }
    
    if (!companyName || !position) {
      setError("Le nom de l'entreprise et le poste sont requis.");
      return;
    }
    
    setError(null);
    setSuccess(null);
    setIsGenerating(true);
    
    try {
      const selectedBenefits = benefits.filter(b => b.selected).map(b => `${b.icon} ${b.label}`);
      
      // Extract document contents
      const documentContents = documents
        .map(doc => doc.content)
        .filter((content): content is string => content !== undefined);

      const post = await generateJobPost(
        companyName,
        position,
        location,
        experience,
        skills,
        contractType,
        tone,
        jobLink,
        companyValues,
        companyWebsite,
        undefined,
        selectedBenefits,
        isBlinded,
        {
          sector,
          replacements: []
        },
        documentContents // Pass document contents to the generation function
      );
      
      setGeneratedPost(post);
      setEditableText(post);
      setSuccess('Offre générée avec succès !');
      
      await decrementCredits(user.id);
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la génération.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleRegenerate = async () => {
    if (!user?.id) {
      setError('Veuillez vous connecter pour régénérer une offre');
      return;
    }

    const hasCredits = await hasRemainingCredits(user.id);
    if (!hasCredits) {
      setError('Vous avez épuisé vos crédits. Veuillez passer à un forfait supérieur.');
      return;
    }
    
    setError(null);
    setSuccess(null);
    setIsGenerating(true);
    
    try {
      const selectedBenefits = benefits.filter(b => b.selected).map(b => `${b.icon} ${b.label}`);
      
      // Extract document contents
      const documentContents = documents
        .map(doc => doc.content)
        .filter((content): content is string => content !== undefined);
      
      const post = await generateJobPost(
        companyName,
        position,
        location,
        experience,
        skills,
        contractType,
        tone,
        jobLink,
        companyValues,
        companyWebsite,
        undefined,
        selectedBenefits,
        isBlinded,
        {
          sector,
          replacements: []
        },
        documentContents // Pass document contents to the generation function
      );
      
      setGeneratedPost(post);
      setEditableText(post);
      setSuccess('Offre régénérée avec succès !');
      
      await decrementCredits(user.id);
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la régénération.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
    setSuccess('Texte copié dans le presse-papier !');
    setTimeout(() => setSuccess(null), 3000);
  };
  
  const handleExport = () => {
    if (!editableText) {
      setError('Aucune offre à exporter');
      return;
    }

    try {
      const blob = new Blob([editableText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `offre-${position || 'emploi'}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Offre exportée avec succès !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Une erreur est survenue lors de l\'export');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleSave = async () => {
    if (!user?.id) {
      setError('Veuillez vous connecter pour sauvegarder une offre');
      return;
    }

    if (!editableText) {
      setError('Aucune offre à sauvegarder');
      return;
    }

    try {
      const postToSave = {
        title: position || 'Sans titre',
        company: companyName,
        content: editableText,
        formData: {
          companyName,
          position,
          location,
          experience,
          skills,
          contractType,
          tone,
          jobLink,
          companyValues,
          isBlinded,
          sector
        }
      };

      await saveJobPost(user.id, postToSave);
      setSuccess('Votre offre a été sauvegardée !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la sauvegarde');
      setTimeout(() => setError(null), 3000);
    }
  };

  const selectedToneOption = toneOptions.find(option => option.id === tone);
  const selectedContractOption = contractOptions.find(option => option.id === contractType);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Générer une offre d'emploi</h2>
      
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column - Input form */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Informations sur l'offre</h3>
            <button
              onClick={() => setIsBlinded(!isBlinded)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                isBlinded 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title="Masquer les informations sensibles de l'entreprise"
            >
              <EyeOff className="w-4 h-4" />
              <span>Blinder l'offre</span>
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Company Information Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'entreprise *
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: TechInnovate"
                  required
                />
                {isBlinded && (
                  <p className="mt-1 text-sm text-gray-500">
                    Ce nom sera remplacé par une description générique dans l'offre
                  </p>
                )}
              </div>

              {isBlinded && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secteur d'activité
                  </label>
                  <select
                    value={sector}
                    onChange={(e) => setSector(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un secteur</option>
                    <option value="tech">Technologie</option>
                    <option value="retail">Commerce</option>
                    <option value="finance">Finance</option>
                    <option value="healthcare">Santé</option>
                    <option value="manufacturing">Industrie</option>
                    <option value="consulting">Conseil</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site web de l'entreprise
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="url"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Ex: https://www.entreprise.com"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Le site web sera utilisé pour s'inspirer du ton et des valeurs de l'entreprise
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poste *
              </label>
              <input
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: Développeur Full-Stack"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lieu
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: Paris, France (Remote possible)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expérience requise
              </label>
              <input
                type="text"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: 3+ ans d'expérience"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compétences
              </label>
              <textarea
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: React, Node.js, SQL, NoSQL"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de contrat
              </label>
              <button
                type="button"
                onClick={() => setIsContractModalOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {selectedContractOption ? (
                  <span className="flex items-center">
                    <span className="mr-2">{selectedContractOption.emoji}</span>
                    <span>{selectedContractOption.title}</span>
                  </span>
                ) : (
                  <span>Sélectionner un type de contrat</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ton de l'offre
              </label>
              <button
                type="button"
                onClick={() => setIsToneModalOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {selectedToneOption ? (
                  <span className="flex items-center">
                    <span className="mr-2">{selectedToneOption.emoji}</span>
                    <span>{selectedToneOption.title}</span>
                  </span>
                ) : (
                  <span>Sélectionner un ton</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lien pour postuler
              </label>
              <input
                type="url"
                value={jobLink}
                onChange={(e) => setJobLink(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: https://entreprise.com/carrieres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valeurs de l'entreprise (max 5)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: Innovation"
                  disabled={companyValues.length >= 5}
                />
                <button
                  onClick={handleAddValue}
                  disabled={!newValue.trim() || companyValues.length >= 5}
                  className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
              
              {companyValues.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {companyValues.map((value, index) => (
                    <div key={index} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full flex items-center">
                      <span>{value}</span>
                      <button
                        onClick={() => handleRemoveValue(index)}
                        className="ml-2 text-indigo-500 hover:text-indigo-700"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Les plus du job
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {benefits.map(benefit => (
                  <button
                    key={benefit.id}
                    onClick={() => toggleBenefit(benefit.id)}
                    className={`p-3 rounded-lg border flex items-center space-x-2 transition-colors ${
                      benefit.selected 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-xl">{benefit.icon}</span>
                    <span className="text-sm">{benefit.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Documents complémentaires (PDF uniquement)
              </label>
              
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  isDragActive 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-300 hover:border-indigo-500'
                }`}
              >
                <input {...getInputProps()} />
                <Paperclip className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-gray-600">
                  {isDragActive
                    ? "Déposez les fichiers ici..."
                    : "Glissez-déposez vos fichiers PDF ici, ou cliquez pour sélectionner"}
                </p>
                <p className="text-sm text-gray-500 mt-1">PDF uniquement, 5MB max par fichier</p>
              </div>

              {documents.length > 0 && (
                <div className="space-y-2">
                  {documents.map((doc, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                          <p className="text-xs text-gray-500">
                            {(doc.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <a 
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => removeDocument(doc)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isUploading && (
                <div className="flex items-center justify-center space-x-2 text-indigo-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                  <span>Téléchargement en cours...</span>
                </div>
              )}
            </div>

            <div className="pt-4">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !companyName || !position}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    <span>Générer l'offre</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right column - Output */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Offre générée</h3>
            
            {generatedPost && (
              <div className="flex space-x-2">
                <button
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  <span>Régénérer</span>
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copier</span>
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Sauvegarder</span>
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Exporter</span>
                </button>
                <LinkedInShareButton content={editableText} />
              </div>
            )}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
            {isGenerating ? (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Génération en cours...</p>
                </div>
              </div>
            ) : generatedPost ? (
              <div className="p-6">
                <TextEditor
                  value={editableText}
                  onChange={setEditableText}
                  height="600px"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center text-gray-500">
                  <Wand2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Remplissez le formulaire et cliquez sur "Générer l'offre"</p>
                  <p className="text-sm mt-2">L'offre générée apparaîtra ici</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for tone selection */}
      {isToneModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={toneModalRef}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Ton de l'offre</h3>
              <button 
                onClick={() => setIsToneModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {toneOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setTone(option.id as PostTone);
                    setIsToneModalOpen(false);
                  }}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                    tone === option.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{option.emoji}</div>
                    <div>
                      <div className="font-medium">{option.title}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                    </div>
                    {tone === option.id && (
                      <Check className="ml-auto text-indigo-600 w-5 h-5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal for contract type selection */}
      {isContractModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={contractModalRef}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Type de contrat</h3>
              <button 
                onClick={() => setIsContractModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {contractOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setContractType(option.id as ContractType);
                    setIsContractModalOpen(false);
                  }}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                    contractType === option.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{option.emoji}</div>
                    <div>
                      <div className="font-medium">{option.title}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                    </div>
                    {contractType === option.id && (
                      <Check className="ml-auto text-indigo-600 w-5 h-5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface JobBenefit {
  id: string;
  label: string;
  icon: string;
  selected: boolean;
}

const toneOptions = [
  { id: 'Formelle', title: 'Formelle', description: 'Ton professionnel et conventionnel', emoji: '👔' },
  { id: 'Décontractée', title: 'Décontractée', description: 'Ton amical et accessible', emoji: '😊' },
  { id: 'Provocante', title: 'Provocante', description: 'Ton audacieux qui attire l\'attention', emoji: '🔥' },
  { id: 'Inspirante', title: 'Inspirante', description: 'Ton motivant et enthousiaste', emoji: '✨' }
];

const contractOptions = [
  { id: 'CDI', title: 'CDI', description: 'Contrat à durée indéterminée', emoji: '📝' },
  {
    id: 'CDD',
    title: 'CDD',
    description: 'Contrat à durée déterminée',
    emoji: '⏱️'
  },
  {
    id: 'Freelance',
    title: 'Freelance',
    description: 'Mission indépendante',
    emoji: '🚀'
  },
  {
    id: 'Stage',
    title: 'Stage',
    description: 'Formation professionnelle',
    emoji: '🎓'
  },
  {
    id: 'Alternance',
    title: 'Alternance',
    description: 'Formation en alternance',
    emoji: '🔄'
  }
];

export default JobPostGenerator;